import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Loader2, Target, Tag } from 'lucide-react';
import { useApiClient } from '../hooks/useApiClient';
import { CorePriority } from '../types/corePriority';
import { normalizeGoals } from '../utils/corePriorityGoals';
import { formatDateInIST } from '../utils/mappers';

interface CorePrioritiesViewProps {
  employeeId: string;
  employeeName: string;
  year: number;
}

export function CorePrioritiesView({ employeeId, employeeName, year }: CorePrioritiesViewProps) {
  const { apiCall } = useApiClient();
  const [isExpanded, setIsExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [corePriority, setCorePriority] = useState<CorePriority | null>(null);

  useEffect(() => {
    if (isExpanded && !corePriority) {
      fetchCorePriority();
    }
  }, [isExpanded, employeeId, year]);

  const fetchCorePriority = async () => {
    setLoading(true);
    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/CorePriorities/employee/${encodeURIComponent(employeeId)}/year/${year}`
      );
      
      if (response.ok) {
        const result = await response.json();
        if (result.data) {
          setCorePriority(result.data);
        } else {
          setCorePriority(null);
        }
      } else {
        setCorePriority(null);
      }
    } catch (error) {
      console.error('Error fetching core priorities:', error);
      setCorePriority(null);
    } finally {
      setLoading(false);
    }
  };

  const goals = corePriority ? normalizeGoals(corePriority) : [];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 mb-6 shadow-sm overflow-hidden">
      {/* Header — always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-6 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-xl">
            <Target className="w-6 h-6 text-purple-500" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-slate-800">
              Core Priorities — {year}
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              {employeeName} {corePriority ? `· ${goals.length || 1} goal${goals.length !== 1 ? 's' : ''} set` : ''}
              {corePriority?.modifiedDate && ` · Updated ${formatDateInIST(corePriority.modifiedDate)}`}
            </p>
          </div>
        </div>
        <div className={`p-2 rounded-lg transition-colors ${isExpanded ? 'bg-purple-100' : 'bg-slate-100'}`}>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-purple-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </button>

      {/* Expandable content */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-6">
          <div className="border-t border-slate-100 pt-5">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                <span className="ml-3 text-slate-500 font-medium">Loading priorities...</span>
              </div>
            ) : corePriority && (corePriority.priorities || goals.length > 0) ? (
              <>
                {goals.length > 0 ? (
                  <div className="space-y-4">
                    {goals.map((goal, index) => (
                      <div
                        key={`goal-${index}`}
                        className="bg-gradient-to-br from-slate-50 to-purple-50/30 border border-slate-200 rounded-xl p-5"
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3">
                            <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-sm">
                              {index + 1}
                            </span>
                            <h4 className="text-base font-semibold text-slate-800">{goal.goal}</h4>
                          </div>
                          {goal.weightage && (
                            <span className="flex-shrink-0 text-sm bg-pink-100 text-pink-600 border border-pink-200 rounded-full px-3 py-1 font-semibold">
                              {goal.weightage}%
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap mb-3 ml-11">
                          {goal.description}
                        </p>

                        <div className="flex flex-wrap gap-2 ml-11">
                          {goal.category && (
                            <span className="inline-flex items-center gap-1.5 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded-lg px-2.5 py-1 font-medium">
                              <Tag className="w-3 h-3" />
                              {goal.category}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-slate-600 text-sm whitespace-pre-wrap leading-relaxed bg-slate-50 rounded-xl p-4 border border-slate-200">
                    {corePriority.priorities}
                  </div>
                )}
                
                <div className="flex items-center justify-between text-xs text-slate-400 pt-4 mt-4 border-t border-slate-100">
                  <span>Last updated: {formatDateInIST(corePriority.modifiedDate)}</span>
                  <span className="italic text-slate-400">View only — set by your manager</span>
                </div>
              </>
            ) : (
              <div className="text-center py-10">
                <div className="p-4 bg-slate-100 rounded-2xl inline-block mb-3">
                  <Target className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-600 font-medium">No core priorities set for {year}</p>
                <p className="text-sm text-slate-400 mt-1">
                  Your manager can set priorities through the Core Priorities tab
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
